import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import ActorChip from '../components/audit/ActorChip';

interface ReviewReport {
  id: string;
  reviewId: string;
  garageId: string;
  reportedByUserId: string;
  reason: string;
  status: string;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  createdAt: string;
  /** Spec 069 — resolved names (null when the resolver could not name the actor). */
  reportedByName?: string | null;
  reviewedByAdminName?: string | null;
}

interface ReviewContent {
  id: string;
  garageId: string;
  garageName: string;
  serviceName: string;
  carLabel: string;
  rating: number;
  comment: string;
  status: string;
  createdAt: string;
  garageResponse: string | null;
}

interface PagedReports {
  content: ReviewReport[];
  totalPages: number;
  number: number;
  totalElements: number;
}

const STATUS_TABS = ['PENDING', 'REVIEWED', 'DISMISSED'] as const;
type StatusTab = typeof STATUS_TABS[number];

function Stars({ rating }: { rating: number }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  );
}

export default function ReportedReviewsPage() {
  const auth = useAuth();
  // Spec 069 — `?status=` preselects the tab (dashboard tile); unknown values fall back to Pending.
  const [searchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status') as StatusTab | null;
  const [status, setStatus] = useState<StatusTab>(
    requestedStatus && STATUS_TABS.includes(requestedStatus) ? requestedStatus : 'PENDING',
  );
  const [page, setPage] = useState(0);
  const [reports, setReports] = useState<ReviewReport[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reviewCache, setReviewCache] = useState<Record<string, ReviewContent>>({});
  const [moderating, setModerating] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<PagedReports>(
        `/api/admin/reviews/reported?status=${status}&page=${page}&size=20`,
        auth.token,
      );
      setReports(data.content ?? []);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [auth.token, status, page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const fetchReview = async (reviewId: string, garageId?: string) => {
    if (reviewCache[reviewId] || !garageId || !auth.token) return;
    try {
      const data = await api.get<{ content: ReviewContent[] }>(
        `/api/garages/${garageId}/reviews?page=0&size=50`,
        auth.token,
      );
      const review = data.content?.find(r => r.id === reviewId);
      if (review) {
        setReviewCache(prev => ({ ...prev, [reviewId]: review }));
      }
    } catch {
      // Non-critical — report still shows without review content
    }
  };

  const moderate = async (reviewId: string, action: 'REMOVE' | 'DISMISS') => {
    if (!auth.token) return;
    setModerating(reviewId + action);
    try {
      await api.put(`/api/admin/reviews/${reviewId}/moderate`, { action }, auth.token);
      await fetchReports();
      setExpanded(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Moderation failed');
    } finally {
      setModerating(null);
    }
  };

  const statusColor = (s: string) => {
    if (s === 'PENDING') return { bg: '#fef3c7', fg: '#854d0e' };
    if (s === 'REVIEWED') return { bg: '#d1fae5', fg: '#065f46' };
    return { bg: '#e5e7eb', fg: '#374151' };
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Reported Reviews</h1>
          {!loading && <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{totalElements} report{totalElements !== 1 ? 's' : ''}</p>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => { setStatus(tab); setPage(0); }}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: status === tab ? '#030c54' : '#d1d5db',
                background: status === tab ? '#030c54' : 'white',
                color: status === tab ? 'white' : '#374151',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tab.charAt(0) + tab.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading...</p>
      ) : error ? (
        <p style={{ color: '#dc2626' }}>{error}</p>
      ) : reports.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No {status.toLowerCase()} reports.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {reports.map(report => {
            const isOpen = expanded === report.id;
            const review = reviewCache[report.reviewId];
            const sc = statusColor(report.status);
            return (
              <div
                key={report.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'white',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.875rem 1rem',
                    cursor: 'pointer',
                  }}
                  data-testid="report-row"
                  onClick={() => {
                    setExpanded(isOpen ? null : report.id);
                    if (!isOpen && review === undefined) {
                      fetchReview(report.reviewId, report.garageId);
                    }
                  }}
                >
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace', flexShrink: 0 }}>
                    Review {report.reviewId.slice(0, 8)}…
                  </span>
                  <span style={{
                    padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                    background: sc.bg, color: sc.fg, flexShrink: 0,
                  }}>
                    {report.status}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.875rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {report.reason}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }}>
                    {new Date(report.createdAt).toLocaleDateString()}
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '1rem', background: '#fafafa' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem' }}>Report reason</p>
                        <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>{report.reason}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem' }}>Reported by</p>
                        <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }} data-testid="reported-by">
                          <ActorChip role="CUSTOMER" name={report.reportedByName} subjectId={report.reportedByUserId} />
                        </p>
                      </div>
                    </div>

                    {review ? (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.875rem', background: 'white', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Stars rating={review.rating} />
                          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{review.serviceName} · {review.carLabel}</span>
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: 'auto' }}>{review.garageName}</span>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>{review.comment || <em style={{ color: '#9ca3af' }}>No comment</em>}</p>
                        {review.garageResponse && (
                          <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0.5rem 0 0', fontStyle: 'italic' }}>
                            Garage reply: {review.garageResponse}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: '0.75rem', background: '#f3f4f6', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                        Review ID: <span style={{ fontFamily: 'monospace' }}>{report.reviewId}</span>
                      </div>
                    )}

                    {report.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          onClick={() => moderate(report.reviewId, 'REMOVE')}
                          disabled={moderating !== null}
                          style={{
                            padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none',
                            background: '#dc2626', color: 'white', fontWeight: 600,
                            fontSize: '0.8125rem', cursor: moderating ? 'not-allowed' : 'pointer', opacity: moderating ? 0.7 : 1,
                          }}
                        >
                          {moderating === report.reviewId + 'REMOVE' ? 'Removing…' : 'Remove review'}
                        </button>
                        <button
                          onClick={() => moderate(report.reviewId, 'DISMISS')}
                          disabled={moderating !== null}
                          style={{
                            padding: '0.5rem 1.25rem', borderRadius: '6px',
                            border: '1px solid #d1d5db', background: 'white',
                            color: '#374151', fontWeight: 600, fontSize: '0.8125rem',
                            cursor: moderating ? 'not-allowed' : 'pointer', opacity: moderating ? 0.7 : 1,
                          }}
                        >
                          {moderating === report.reviewId + 'DISMISS' ? 'Dismissing…' : 'Dismiss report'}
                        </button>
                      </div>
                    )}

                    {report.status !== 'PENDING' && report.reviewedAt && (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }} data-testid="reviewed-by">
                        {report.status === 'REVIEWED' ? 'Removed' : 'Dismissed'} on {new Date(report.reviewedAt).toLocaleDateString()}
                        {report.reviewedByAdminId && (
                          <> · Reviewed by <ActorChip role="ADMIN" name={report.reviewedByAdminName} subjectId={report.reviewedByAdminId} /></>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: page === 0 ? 'not-allowed' : 'pointer', color: '#374151' }}
          >
            ‹
          </button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', color: '#374151' }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
